<template>
  <div
    v-if="match"
    class="c-black match-listing"
  >
    <v-row
      class="d-flex align-top"
      dense
    >
      <v-col cols="11">
        <div
          v-if="match.accepts_subsidy || match.closed_status != 'Open'"
          class="mb-3"
        >
          <v-chip
            v-if="match.closed_status != 'Open'"
            class="me-2"
            color="red"
            label
          >
            <v-icon
              class="c-white fs-16 me-2"
              icon="warning"
              size="24"
            />

            <span class="fw-600">
              {{ $bct(match.closed_status) }}
            </span>
          </v-chip>

          <v-chip
            v-if="match.accepts_subsidy"
            color="secondary"
            variant="outlined"
            label
          >
            {{ $bct('Accepts Financial Aid') }}
          </v-chip>
        </div>

        <h2
          class="c-black fw-600 fs-20"
          data-cy="match-name"
          dense
        >
          {{ match.name }}
        </h2>

        <div
          v-if="match.license_business_name != match.name"
          class="fs-16"
        >
          {{ match.license_business_name }}
        </div>
      </v-col>

      <v-col
        class="d-flex justify-end"
        cols="1"
      >
        <Favorite
          v-if="search.group_id"
          @favorite:add="$emit('favorite:add', $event)"
          @favorite:list="$emit('favorite:list', $event)"
          @favorite:remove="$emit('favorite:remove', $event)"
          :ref="'favorite_' + match.id"
          :favorites="favorites"
          :lists="lists"
          :provider-id="match.id"
        />
      </v-col>
    </v-row>

    <v-row
      class="ma-0 pa-0"
      dense
    >
      <v-col>
        <span
          class="fs-16 fw-400"
          data-cy="match-address"
        >
          <span :class="hiddenAddress">
            {{ formatAddress(match.address1, match.city, match.zip) }}
          </span>
          ,
          {{
            match.driving_distance ?? match.distance_from_origin ?? match.distance_from_destination
          }}
          {{ $bct('miles away') }}
        </span>
      </v-col>
    </v-row>

    <v-row
      v-if="schema.meta.card.enable_summary"
      class="mb-1"
      data-cy="match-summary"
      dense
    >
      <v-col class="px-3">
        <v-list
          class="pb-0"
          role="list"
          slim
        >
          <v-list-item
            v-if="match.quality_rating_score > 0"
            :min-height="0"
            class="pa-0 my-1"
            role="listitem"
          >
            <template #prepend>
              <img
                v-if="store.state.site.ratings.icons && $store.state.site.ratings.icons.length > 0"
                :alt="store.state.site.ratings.name + ' rating'"
                :src="store.state.site.ratings.icons[0].pass"
                class="d-flex"
                height="24"
                width="24"
              />

              <v-icon
                v-else
                alt=""
                color="#FFD200"
                icon="star"
                role="presentation"
                size="24"
              />
            </template>

            <v-list-item-title
              v-if="match.quality_rating_score"
              class="fs-16 fw-400"
            >
              <div class="fs-16 fw-400 lh-24">
                <span>{{ $bct(store.state.site.ratings.name) }}</span>
                <span>:&nbsp;</span>
                <span>{{ qualityRatingText }}</span>
              </div>
            </v-list-item-title>

            <v-list-item-title
              v-else
              class="fs-16 fw-400"
            >
              {{ $bct('No state rating.') }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="match.is_apost"
            :min-height="0"
            class="pa-0 my-1"
            role="listitem"
          >
            <template #prepend>
              <img
                :src="$a.url('icons.apost')"
                alt="APOST quality rating icon"
                height="24"
              />
            </template>

            <v-list-item-title class="fs-16 fw-400">
              <span>{{ $bct('APOST Quality Campaign Member') }}</span>
            </v-list-item-title>
          </v-list-item>

          <template
            v-if="
              schema.meta.openings.enabled && (schema.meta.openings.public || $role == 'specialist')
            "
          >
            <v-list-item
              v-for="(spot, index) in match.vacancy_descriptions"
              v-show="spot.vacant != null"
              :key="index"
              :min-height="0"
              class="pa-0 my-1"
              role="listitem"
            >
              <template #prepend>
                <v-icon
                  v-if="spot.vacant"
                  class="c-green"
                  icon="check_circle"
                  size="24"
                />

                <v-icon
                  v-else
                  class="c-red"
                  icon="cancel"
                  size="24"
                />
              </template>

              <v-list-item-title class="fs-16 fw-400">
                <span v-if="spot.vacant">
                  {{ `${$bct('Openings available')} : ` }}
                </span>

                <span v-else>
                  {{ $bct('Openings not available for') }}
                </span>

                <LongDate
                  :date="spot.start_date"
                  :prefix="`${monthsToAgeString(spot.min)} - ${monthsToAgeString(spot.max)} ${$bct('as of')}`"
                />
              </v-list-item-title>
            </v-list-item>
          </template>

          <v-list-item
            v-if="schema.meta.card.enable_hours && match.hours_chunks?.length"
            :min-height="0"
            class="pa-0 my-1"
            role="listitem"
          >
            <template #prepend>
              <v-icon
                class="c-black"
                icon="query_builder"
                size="24"
              />
            </template>

            <v-list-item-title class="fs-16 fw-400">
              <HoursChunks
                v-if="match.hours_chunks"
                :chunks="match.hours_chunks"
              />
            </v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="match.payment_subsidies_accepted.length > 0"
            class="pa-0 my-1"
            role="listitem"
          >
            <template #prepend>
              <v-icon
                color="#578052"
                icon="attach_money"
                size="24"
              />
            </template>

            <v-list-item-title class="c-black fs-16 fw-400">
              {{ $bct('Financial assistance options available') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>

    <v-row
      v-if="match.categories.length > 0"
      class="mb-0 mt-2"
      dense
    >
      <v-col>
        <v-chip
          v-for="(category, index) in match.categories"
          :key="index"
          class="me-1"
        >
          {{ $bct(category) }}
        </v-chip>
      </v-col>
    </v-row>

    <v-row
      v-if="match.private_notice"
      dense
    >
      <v-col class="bg-primary">
        <div class="pa-2 fs-14 c-white">
          <v-icon
            class="c-white fs-16 me-2"
            icon="announcement"
            size="24"
          />
          <span class="fw-600 me-1">{{ $bct('Internal only') }}:</span>
          <span>{{ $bct(match.private_notice) }}</span>
        </div>
      </v-col>
    </v-row>

    <v-row
      v-if="match.public_notice"
      dense
    >
      <v-col class="bg-primary">
        <div class="pt-2 px-2 fs-14 c-white">
          <v-icon
            class="c-white fs-16 me-2"
            icon="info"
            size="24"
          />

          <span class="fw-600 me-1">{{ $bct('Please note') }}:</span>

          <MarkdownContent :content="match.public_notice" />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';

import Favorite from '@/shared/components/search/Favorite.vue';
import { formatAddress } from '@/shared/services/address';
import HoursChunks from '@/public/components/provider/HoursChunks.vue';
import LongDate from '@/shared/components/LongDate.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import useAgeCalculations from '@/shared/composables/useAgeCalculations';
import useQualityRating from '@/shared/composables/useQualityRating';

const $bct = inject('translator');

const store = useStore();
const { monthsToAgeString } = useAgeCalculations();

const props = defineProps({
  favorites: {
    type: Array,
    default: null,
  },
  lists: {
    type: Array,
    default: null,
  },
  match: {
    type: Object,
    default: null,
  },
  schema: {
    type: Object,
    default: null,
  },
  search: {
    type: Object,
    default: null,
  },
});

defineEmits(['favorite:add', 'favorite:list', 'favorite:remove']);

const hiddenAddress = computed(() => (props.match.hide_address ? 'text-grey-darken-2' : ''));
const qualityRatingText = useQualityRating(
  computed(() => props.match?.quality_rating_score),
  computed(() => props.match?.custom?.quality_rating_text_override),
);
</script>

<style>
.match-listing .v-list-item--slim .v-list-item__prepend > img ~ .v-list-item__spacer {
  width: 20px;
}
</style>
